/**
 * Shared service-history PDF builder — the single place that turns a
 * customer's `service_history` rows into a base64 PDF attachment.
 *
 * Used by ServiceHistoryModal (the dedicated "print/download/email" flow) and
 * CustomerEmailModal (the generic composer's "Service history" template) so
 * both entry points attach the identical document instead of one of them
 * silently sending without it.
 */
import { pdf } from '@react-pdf/renderer';
import { supabase } from '../../../lib/supabase';
import { urlToBase64, LOGO_MAX_PX } from '../../../lib/images';
import { reportError } from '../../../lib/errors';
import {
    ServiceHistoryPDF,
    type ServiceHistoryIdentity,
    type ServiceHistoryVehicleGroup,
    type ServiceRecord,
} from './ServiceHistoryPDF';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';

export interface BuildServiceHistoryPdfArgs {
    orgId: string;
    customerId: string;
    /** Scope to one vehicle; omit for all of the customer's vehicles. */
    vehicleId?: string;
    customerName: string;
    customerEmail?: string;
    customerPhone?: string;
    showPrices?: boolean;
}

export interface BuiltServiceHistoryPdf {
    pdfBase64: string;
    fileName: string;
    recordCount: number;
}

interface OrgIdentityRow {
    name?: string | null;
    branding?: { logo_url?: string; invoice_accent_color?: string; primary_color?: string } | null;
    business_address?: string | null;
    business_phone?: string | null;
    business_email?: string | null;
    vat_number?: string | null;
    subscription_tier?: string | null;
}

interface ServiceHistoryRow {
    service_date: string;
    mileage?: number | null;
    mileage_source?: string | null;
    title?: string | null;
    summary?: string | null;
    line_items?: ServiceRecord['line_items'];
    total?: number | null;
    vehicle_id?: string | null;
    vehicles?: ServiceHistoryVehicleGroup['vehicle'];
}

export async function buildServiceHistoryPdfBase64(
    args: BuildServiceHistoryPdfArgs
): Promise<BuiltServiceHistoryPdf | null> {
    const { orgId, customerId, vehicleId, customerName, customerEmail, customerPhone, showPrices = false } = args;

    try {
        // 1. Live org identity for the header.
        const { data: org, error: orgError } = await supabase
            .from('organizations')
            .select('name, branding, business_address, business_phone, business_email, vat_number, subscription_tier')
            .eq('id', orgId)
            .single<OrgIdentityRow>();
        if (orgError) reportError(orgError, 'build-service-history-pdf.organizations');

        const branding = org?.branding || {};
        const identity: ServiceHistoryIdentity = {
            name: org?.name || 'Your Workshop',
            logo_url: branding.logo_url || undefined,
            address: org?.business_address || undefined,
            phone: org?.business_phone || undefined,
            email: org?.business_email || undefined,
            vat_number: org?.vat_number || undefined,
            accent: branding.invoice_accent_color || branding.primary_color || DEFAULT_DOCUMENT_ACCENT,
            tier: org?.subscription_tier || 'free',
        };

        // 2. Service history records (+ vehicle), scoped if a vehicle is given.
        //    Apply all .eq() filters BEFORE .order() — .order() returns a
        //    transform builder that no longer exposes .eq().
        let query = supabase
            .from('service_history')
            .select('service_date, mileage, mileage_source, title, summary, line_items, total, vehicle_id, vehicles(vrm, make, model, year)')
            .eq('customer_id', customerId);
        if (vehicleId) query = query.eq('vehicle_id', vehicleId);

        const { data: rows, error: shError } = await query
            .order('service_date', { ascending: false })
            .returns<ServiceHistoryRow[]>();
        if (shError) throw shError;

        // 3. Group by vehicle (records already newest-first).
        const groupsMap = new Map<string, ServiceHistoryVehicleGroup>();
        for (const r of rows ?? []) {
            const key = r.vehicle_id || 'none';
            if (!groupsMap.has(key)) {
                groupsMap.set(key, { vehicle: r.vehicles || null, records: [] });
            }
            const rec: ServiceRecord = {
                service_date: r.service_date,
                mileage: r.mileage,
                mileage_source: r.mileage_source,
                title: r.title,
                summary: r.summary,
                line_items: Array.isArray(r.line_items) ? r.line_items : [],
                total: r.total,
            };
            groupsMap.get(key)!.records.push(rec);
        }
        const groups = Array.from(groupsMap.values());
        const recordCount = groups.reduce((n, g) => n + g.records.length, 0);

        // 4. Logo → base64 so it renders in the PDF (CORS-safe).
        let logoBase64: string | undefined;
        if (branding.logo_url) {
            try {
                logoBase64 = (await urlToBase64(branding.logo_url, { maxDimension: LOGO_MAX_PX })) ?? undefined;
            } catch (e) {
                console.error('Logo conversion failed:', e);
            }
        }

        // 5. Single-vehicle scope → use its reg in the filename.
        const singleVehicleReg = vehicleId && groups.length === 1 ? groups[0].vehicle?.vrm : undefined;
        const fileName = `Service_History_${(singleVehicleReg || customerName || 'customer').replace(/[^a-z0-9]+/gi, '_')}.pdf`;

        // 6. Render + base64-encode.
        const doc = (
            <ServiceHistoryPDF
                identity={{ ...identity, logoBase64 }}
                customer={{ full_name: customerName, email: customerEmail, phone: customerPhone }}
                groups={groups}
                showPrices={showPrices}
            />
        );
        const blob = await pdf(doc).toBlob();
        const pdfBase64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        return { pdfBase64, fileName, recordCount };
    } catch (err) {
        console.error('buildServiceHistoryPdfBase64 failed:', err);
        return null;
    }
}
